'use client';

import React, { useEffect, useState } from 'react';
import { Button } from '@nextui-org/react';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import {
  COOKIE_SETTINGS_OPEN_EVENT,
  setCookieConsent,
} from '@/utility/consent/cookieConsent';
import { PRIVACY_POLICY_URL } from '@/constants/apiConstants';
import CookieSettingsModal from './CookieSettingsModal';

const CookieConsentBanner: React.FC = () => {
  const { consent, ready } = useCookieConsent();
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const open = () => setSettingsOpen(true);
    window.addEventListener(COOKIE_SETTINGS_OPEN_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_OPEN_EVENT, open);
  }, []);

  const showBanner = ready && !consent && !settingsOpen;

  return (
    <>
      {showBanner && (
        <div
          role="region"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200 bg-white px-5 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
        >
          <div className="mx-auto flex max-w-[1100px] flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-[14px] text-gray-700">
              We use necessary cookies to keep the app working. With your
              permission we also use Google Analytics to understand how the app
              is used.{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Privacy policy
              </a>
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button
                size="sm"
                radius="sm"
                variant="light"
                onPress={() => setSettingsOpen(true)}
              >
                Cookie settings
              </Button>
              <Button
                size="sm"
                radius="sm"
                variant="bordered"
                className="border-primary text-primary"
                onPress={() => setCookieConsent(false)}
              >
                Reject
              </Button>
              <Button
                size="sm"
                radius="sm"
                variant="bordered"
                className="border-primary text-primary"
                onPress={() => setCookieConsent(true)}
              >
                Accept analytics
              </Button>
            </div>
          </div>
        </div>
      )}
      <CookieSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialAnalytics={consent?.analytics ?? false}
      />
    </>
  );
};

export default CookieConsentBanner;
